import { LANGUAGE_TO_LANGUAGE_HEADER, t } from '../localization';
import { percentage } from '../localization';
import { getLocalizedMonthName, toLocaleDateString } from './dates';
import { getPredefinedRanges } from './filters';

export const getAssetGroupKey = (assetClass) => {
  let getAssetGroupKeyValue;

  switch (assetClass.toLowerCase()) {
    case 'av':
      getAssetGroupKeyValue = 'alternativeGrowth';
      break;
    case 'mv':
      getAssetGroupKeyValue = 'marketGrowth';
      break;
    case 's':
      getAssetGroupKeyValue = 'stability';
      break;
    default:
      return null;
  }

  return getAssetGroupKeyValue;
};

export const getAssetClassName = (
  assetClass,
  short,
  useNewAssetClassStructure = true
) => {
  let assetClassName;

  switch (assetClass?.toLowerCase()) {
    case 'av':
      assetClassName = short
        ? t('app:assetClasses:short:alternativeGrowth')
        : t('app:assetClasses:alternativeGrowth');
      break;
    case 'mv':
      assetClassName = t('app:assetClasses:marketGrowth');
      break;
    case 's':
      assetClassName = t('app:assetClasses:stability');
      break;
    case 'b':
      assetClassName = t('app:assetClasses:moneyMarket');
      break;
    case 'g':
      assetClassName = t('app:assetClasses:hedgeFund');
      break;
    case 'i':
      assetClassName = t('app:assetClasses:property');
      break;
    case 'p':
      assetClassName = t('app:assetClasses:privateEquity');
      break;
    case 'o':
      // @deprecated - After migration this is not in use
      assetClassName = t('app:assetClasses:domesticBonds');
      break;
    case 'd':
      // @deprecated - After migration this is not in use
      assetClassName = t('app:assetClasses:domesticShares');
      break;
    case 'e':
      // @deprecated - After migration we use 'equities'
      assetClassName = t('app:assetClasses:foreignShares');
      break;
    case 't':
      // @deprecated - After migration we use 'bonds'
      assetClassName = t('app:assetClasses:foreignBonds');
      break;
    default:
      return null;
  }

  if (useNewAssetClassStructure) {
    switch (assetClass.toLowerCase()) {
      case 'e':
        assetClassName = t('app:assetClasses:equities');
        break;
      case 't':
        assetClassName = t('app:assetClasses:bonds');
        break;
    }
  }
  return assetClassName;
};

export const getPortfolioGeoTagsKey = (key, assetClass, operatingCountry) => {
  let portfolioTag;

  switch (key) {
    // Geo tags
    case 'GLOBAL':
      if (assetClass === 't') {
        portfolioTag = t('app:portfolio:tags:globalBonds');
      } else if (assetClass === 'e') {
        portfolioTag = t('app:portfolio:tags:globalEquities');
      }
      break;
    case 'HOME':
      if (assetClass === 't') {
        portfolioTag = t('app:portfolio:tags:homeMarketBonds', {
          context: operatingCountry,
        });
      } else if (assetClass === 'e') {
        portfolioTag = t('app:portfolio:tags:homeMarketEquities', {
          context: operatingCountry,
        });
      }
      break;
    // Fallback
    case 'NOT_CLASSIFIED':
      portfolioTag = t('app:portfolio:tags:notClassified');
      break;
    case 'NAP':
      portfolioTag = null;
      break;
    default:
      portfolioTag = key;
      break;
  }

  return portfolioTag;
};

export const getPortfolioMarketTagsKey = (key, assetClass) => {
  let portfolioTag;

  switch (key) {
    // Market tags
    case 'GLOBAL':
      portfolioTag = t('app:portfolio:tags:globalMandate');
      break;
    case 'DEVELOPED':
      portfolioTag = t('app:portfolio:tags:developedmarkets');
      break;
    case 'EMERGING':
    case 'EMG':
      portfolioTag = t('app:portfolio:tags:emergingMarkets');
      break;

    // Fallback
    case 'NOT_CLASSIFIED':
      portfolioTag = t('app:portfolio:tags:notClassified');
      break;
    case 'NAP':
      portfolioTag = null;
      break;
    default:
      portfolioTag = key;
      break;
  }

  return portfolioTag;
};

export const getPortfolioCreditQualityTagsKey = (key, assetClass) => {
  let portfolioTag;

  switch (key) {
    // Credit quality tags
    case 'HIGH_YIELD':
      portfolioTag = t('app:portfolio:tags:highYield');
      break;
    case 'INVESTMENT_GRADE':
      portfolioTag = t('app:portfolio:tags:investmentGrade');
      break;

    // Fallback
    case 'NOT_CLASSIFIED':
      portfolioTag = t('app:portfolio:tags:notClassified');
      break;
    case 'NAP':
      portfolioTag = null;
      break;
    default:
      portfolioTag = key;
      break;
  }

  return portfolioTag;
};

export const isPartOfTheAssetClassGroup = (assetGroup, assetClass) => {
  if (assetGroup === 'MV') {
    // @deprecated - assetClass 'd' is deprecated and will be removed in the future
    if (assetClass === 'd' || assetClass === 'e') return true;
  }
  if (assetGroup === 'AV') {
    if (assetClass === 'p' || assetClass === 'g' || assetClass === 'i')
      return true;
  }
  if (assetGroup === 'S') {
    // @deprecated - assetClass 'o' is deprecated and will be removed in the future
    if (assetClass === 'o' || assetClass === 'b' || assetClass === 't')
      return true;
  }
  return false;
};

export const formatGainValue = (value, renderSuffix = true) => {
  const prefix = value > 0 ? '+' : value < 0 ? '-' : '';
  return ` ${prefix} ${Math.round(Math.abs(value))
    .toString()
    .replace(/(\d)(?=(\d{3})+$)/g, '$1 ')} ${renderSuffix ? 'kr' : ''}`;
};

export const formatTwrValue = (
  value,
  numberOfDecimals = 1,
  fixedNumberOfDecimals
) => {
  const prefix = value > 0 ? '+' : value < 0 ? '-' : '';
  // A null TWR is missing data, not 0 %: let percentage render its "-".
  return `${prefix}${percentage(
    value === null ? value : Math.abs(value) * 100,
    numberOfDecimals,
    fixedNumberOfDecimals
  )}`;
};

export const formatTwrTicks = (
  value,
  numberOfDecimals = 1,
  alwaysUsePrefix
) => {
  let prefix = value < 0 ? '-' : '';
  prefix = alwaysUsePrefix ? (value < 0 ? '-' : value > 0 ? '+' : '') : prefix;
  return `${prefix}${percentage(Math.abs(value) * 100, numberOfDecimals)}`;
};

export const getColorForAssetClass = (assetClass, colorScheme) => {
  switch (assetClass) {
    case 'b':
      return colorScheme[0];
    case 'o':
      // @deprecated - assetClass 'o' is deprecated and will be removed in the future
      return colorScheme[0];
    case 't':
      return colorScheme[0];
    case 'd':
      // @deprecated - assetClass 'o' is deprecated and will be removed in the future
      return colorScheme[1];
    case 'e':
      return colorScheme[1];
    case 'g':
      return colorScheme[2];
    case 'i':
      return colorScheme[2];
    case 'p':
      return colorScheme[2];
    default:
      return null;
  }
};

export const getColorForAssetClassGroup = (assetClass, colorScheme) => {
  switch (assetClass) {
    case 'MV':
      return colorScheme[1];
    case 'AV':
      return colorScheme[2];
    case 'S':
      return colorScheme[0];
    default:
      return null;
  }
};

export const getAssetClassGroupByAssetClass = (assetClass) => {
  switch (assetClass) {
    case 'b':
    // @deprecated - assetClass 'o' is deprecated and will be removed in the future
    case 'o':
    case 't':
      return 'S';
    // @deprecated - assetClass 'o' is deprecated and will be removed in the future
    case 'd':
    case 'e':
      return 'MV';
    case 'g':
    case 'i':
    case 'p':
      return 'AV';
    default:
      return null;
  }
};

export const getAssetClassByAssetClassGroup = (
  assetClassGroup,
  useNewAssetClassStructure = true
) => {
  switch (assetClassGroup) {
    case 'S':
      return useNewAssetClassStructure ? ['b', 't'] : ['b', 'o', 't'];
    case 'MV':
      return useNewAssetClassStructure ? ['e'] : ['d', 'e'];
    case 'AV':
      return ['g', 'i', 'p'];
    default:
      return [];
  }
};

export const getAssetClassesByAssetClassGroup = (
  assetGroup,
  useNewAssetClassStructure = true
) => {
  switch (assetGroup) {
    case 'S': {
      return useNewAssetClassStructure ? ['t', 'b'] : ['o', 't', 'b'];
    }
    case 'MV':
      return useNewAssetClassStructure ? ['e'] : ['e', 'd'];
    case 'AV':
      return ['p', 'i', 'g'];
    default:
      return null;
  }
};

export const formatDateRangeLabel = (startDate, endDate, long) => {
  const start = new Date(startDate);
  const startMonth = getLocalizedMonthName(start.getMonth(), long, true);
  const startYear = start.getFullYear();

  let displayText = `${startMonth} ${startYear}`;

  if (endDate) {
    const end = new Date(endDate);
    const endMonth = getLocalizedMonthName(end.getMonth(), long, true);
    const endYear = end.getFullYear();

    if (startMonth !== endMonth || startYear !== endYear) {
      displayText += ` - ${endMonth} ${endYear}`;
    }
  }

  return displayText;
};

export const formatPeriodLabel = (
  period,
  startDate,
  endDate,
  exact = false,
  long = false
) => {
  if (exact) {
    const { newStartDate, newEndDate } = getPredefinedRanges(period);

    // In economic terms dates always represents the end of day
    // 01.04.24 would not include the gains done on the 1st of April, hence the correct date
    // should be 31.03.24
    const start = new Date(newStartDate || startDate);
    start.setDate(start.getDate() - 1);

    let selectedEndDate = new Date(newEndDate || endDate);
    const today = new Date();
    if (selectedEndDate && selectedEndDate > today) {
      // If the end date is in the future, we use today as the end date
      selectedEndDate = today;
    }

    return `${toLocaleDateString(start)} - ${toLocaleDateString(
      selectedEndDate
    )}`;
  }

  if (period === 'CUSTOM') {
    return formatDateRangeLabel(startDate, endDate, long);
  } else if (period === 'THISMONTH') {
    return getLocalizedMonthName(new Date().getMonth());
  } else {
    return t(`app:filters:period:${period}`);
  }
};

export const truncateString = (string, number) => {
  if (!string) {
    return null;
  }

  if (string.length <= number) {
    return string;
  }
  return string.slice(0, number) + '...';
};

export const sumAssetClassesByAssetGroup = (assetClasses) => {
  return Object.values(
    assetClasses.reduce((accumulator, current) => {
      const assetGroup = getAssetClassGroupByAssetClass(current.assetClass);
      if (!accumulator[assetGroup]) {
        accumulator[assetGroup] = { id: assetGroup, value: 0 };
      }
      accumulator[assetGroup].value += current.strategyShare;
      return accumulator;
    }, {})
  );
};

export const getAssetAllocationGroups = (assetAllocations) =>
  assetAllocations.reduce((accumulator, current) => {
    const assetClassGroup = getAssetClassGroupByAssetClass(current.assetClass);
    if (accumulator.hasOwnProperty(assetClassGroup)) {
      return {
        ...accumulator,
        [assetClassGroup]: {
          strategyShare:
            accumulator[assetClassGroup].strategyShare + current.strategyShare,
          formueStrategyShare:
            accumulator[assetClassGroup].formueStrategyShare +
            current.formueStrategyShare,
        },
      };
    }
    return {
      ...accumulator,
      [assetClassGroup]: {
        ...current,
      },
    };
  }, {});

export const getCSVDownloadLink = (
  urlBase,
  activeFilters,
  chosenLanguage,
  accessToken
) => {
  const ssids = activeFilters.ssids.join(',');
  const taxClasses = [
    ...new Set(activeFilters.taxClass.map((x) => encodeURIComponent(x))),
  ].join(',');
  const accounts = [
    ...new Set(activeFilters.account.map((x) => encodeURIComponent(x))),
  ].join(',');
  const period = activeFilters.period;
  const internal = activeFilters.showInternalPortfolio;
  const external = activeFilters.showExternalPortfolio;
  const language = LANGUAGE_TO_LANGUAGE_HEADER[chosenLanguage];

  let link = urlBase;

  link +=
    `?secret_token=${accessToken}&ssids=${ssids}` +
    `&showInternalPortfolio=${internal}&` +
    `showExternalPortfolio=${external}&taxclass=${taxClasses}` +
    `&account=${accounts}&language=${language}`;

  if (period === 'CUSTOM') {
    const startDate = activeFilters.startDate;
    const _endDate = new Date(activeFilters.endDate);
    const endDate = new Date(
      _endDate.getFullYear(),
      _endDate.getMonth() + 1,
      2 // Add two days to account for timezone issues
    )
      .toISOString()
      .split('T')[0];

    link += `&startDate=${startDate}&endDate=${endDate}`;
  } else {
    link += `&period=${period}`;
  }

  return link;
};
